import React, { useEffect, useId, useRef, useState } from 'react';
import {
  checkInDue, msUntilCheckIn, markCheckInAsked, retireCheckIn, unretireCheckIn, careStore, MUTE_MS,
} from '../../utils/careCheckIn';

/**
 * 🫂 THE CHECK-IN (SPACES_REDESIGN_BRAINSTORM_2026_09_29 §5.12, mockup
 * room-toolkit.html §7, option A): "Heavy stuff tonight." A PRIVATE card for
 * one viewer in a heavy room, never a poll, never a modal, never visible to the
 * room, never in any prompt. A choice, not a warning (warnings raise dread).
 *
 * WHEN (utils/careCheckIn.js owns the rules): `heavy` (the status key
 * heavy_room, backend heavyRoom.js, dark behind CARE_CHECKINS_ENABLED) AND 20+
 * minutes since `enteredAt` (this visit), at most once per space per day per
 * device; "Don't ask again" retires it for this room forever (Undo on the spot).
 * The card keeps its own clock: mount it once and it appears by itself at the
 * 20-minute mark (a timer, no page re-render needed).
 *
 * PROPS: { roomId, heavy, enteredAt, onLighter, onMuteSounds, now?, storage? }
 *  - onLighter()            "Something lighter": the page decides what it does
 *                           (spec: an anonymous world-fiction directive, once a
 *                           room an hour, never "a viewer is upset"). Nothing is
 *                           sent from here.
 *  - onMuteSounds(ms)       optional; when given, "Take a break" offers "Mute room
 *                           sounds for 10 min" and calls it with MUTE_MS.
 *  - now                    tests: a fixed clock (ms). Omit in the page.
 *  - storage                tests: a Storage-like object. Omit in the page.
 *
 * Styling: classes avoid every substring generated themes target (card, panel,
 * chip, message, btn, badge, tag, user, player, header…), buttons carry the
 * theme shield (data-prop-ui) plus their own armor, border-box set on every box.
 * data-care-checkin = the grep/test marker (its value is the state).
 */

export const CHECKIN_COPY = Object.freeze({
  title: 'Heavy stuff tonight.',
  body: 'Still up for this, or want a breather?',
  good: 'I’m good',
  lighter: 'Something lighter',
  pause: 'Take a break',
  only: 'Only you see this.',
  never: 'Don’t ask again',
  breakTitle: 'Good call. The room will be here.',
  tips: ['Water.', 'Stretch.', 'Look outside.'],
  mute: 'Mute room sounds for 10 min',
  muted: 'Muted for 10 min',
  lighterTitle: 'Asking the room to ease up.',
  neverTitle: 'Okay. No more check-ins here.',
  undo: 'Undo',
  close: 'Close',
});

// The one care stylesheet (the crisis card imports it too). Dark by default:
// every room today is dark; a page that sets :root[data-theme="light"] gets the
// mockup's light tokens.
export const CARE_CSS = `
.cck-root, .cck-root * { box-sizing: border-box; }
.cck-root { --cck-bg:#161B25; --cck-line:#2C3850; --cck-ink:#E6ECF7; --cck-soft:#A3B3D2; --cck-accent:#9DBBFF; --cck-btn:#1D2432; --cck-ico:#222B3C;
  position: relative; width: 100%; max-width: 100%; margin: 0 0 8px; padding: 14px 16px 12px; border-radius: 16px; background: var(--cck-bg); box-shadow: inset 0 0 0 1px var(--cck-line); color: var(--cck-ink);
  font: 400 14px/1.4 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif; text-align: left; overflow-wrap: anywhere; animation: cckIn .22s ease-out; }
:root[data-theme="light"] .cck-root { --cck-bg:#EEF3FB; --cck-line:#CCD8EC; --cck-ink:#1B2433; --cck-soft:#4B5D7E; --cck-accent:#2F5FB3; --cck-btn:#FFFFFF; --cck-ico:#DDE7F7; }
.cck-root:focus { outline: none; }
.cck-root:focus-visible { outline: 2px solid var(--cck-accent); outline-offset: 2px; }
.cck-hd { display: flex; align-items: center; gap: 10px; }
.cck-ico { width: 32px; height: 32px; border-radius: 10px; background: var(--cck-ico); color: var(--cck-accent); display: grid; place-items: center; flex: none; }
.cck-ttl { margin: 0; font-size: 15.5px; font-weight: 700; letter-spacing: -.005em; color: var(--cck-ink); }
.cck-bd { margin: 6px 0 12px; font-size: 14px; color: var(--cck-soft); }
.cck-row { display: flex; gap: 8px; flex-wrap: wrap; }
.cck-root .cck-opt.cck-opt { all: unset; box-sizing: border-box; display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 40px; padding: 0 14px !important; margin: 0 !important; border-radius: 10px !important; border: 0 !important;
  background: var(--cck-btn) !important; color: var(--cck-ink) !important; box-shadow: inset 0 0 0 1px var(--cck-line) !important; font: 600 13.5px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important;
  cursor: pointer; text-shadow: none !important; transform: none !important; text-transform: none !important; letter-spacing: 0 !important; min-width: 0 !important; touch-action: manipulation; -webkit-tap-highlight-color: transparent; }
.cck-root .cck-opt.cck-opt:hover { box-shadow: inset 0 0 0 1.5px var(--cck-accent) !important; }
.cck-root .cck-opt.cck-opt:focus-visible { outline: 2px solid var(--cck-accent) !important; outline-offset: 2px; }
.cck-root .cck-opt.cck-opt[disabled] { cursor: default; opacity: .75; }
.cck-ft { display: flex; align-items: center; gap: 6px; margin-top: 12px; font: 500 11.5px/1.2 ui-monospace, "SF Mono", Menlo, monospace; color: var(--cck-soft); }
.cck-sp { flex: 1; }
.cck-root .cck-lnk.cck-lnk { all: unset; box-sizing: border-box; cursor: pointer; padding: 6px 2px !important; margin: 0 !important; color: var(--cck-soft) !important; background: none !important; border: 0 !important; box-shadow: none !important;
  text-decoration: underline !important; text-underline-offset: 3px; font: 500 12.5px/1.2 -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif !important; text-transform: none !important; letter-spacing: 0 !important; }
.cck-root .cck-lnk.cck-lnk:focus-visible { outline: 2px solid var(--cck-accent) !important; outline-offset: 2px; }
.cck-tips { display: flex; flex-wrap: wrap; gap: 14px; margin: 8px 0 12px; font-size: 13.5px; }
.cck-tips span { display: inline-flex; align-items: center; gap: 6px; color: var(--cck-soft); }
.cck-res-list { display: grid; gap: 8px; margin: 12px 0; }
.cck-root a.cck-res.cck-res { display: flex; align-items: center; gap: 12px; min-height: 48px; padding: 11px 12px !important; border-radius: 12px !important; background: var(--cck-btn) !important; box-shadow: inset 0 0 0 1px var(--cck-line) !important;
  color: var(--cck-ink) !important; text-decoration: none !important; border: 0 !important; font: inherit !important; text-shadow: none !important; }
.cck-root a.cck-res.cck-res:hover { box-shadow: inset 0 0 0 1.5px var(--cck-accent) !important; }
.cck-root a.cck-res.cck-res:focus-visible { outline: 2px solid var(--cck-accent) !important; outline-offset: 2px; }
.cck-ri { width: 34px; height: 34px; border-radius: 10px; background: var(--cck-ico); color: var(--cck-accent); display: grid; place-items: center; flex: none; }
.cck-res b { display: block; font-size: 14.5px; font-weight: 700; color: var(--cck-ink); }
.cck-res small { display: block; font-size: 12px; color: var(--cck-soft); }
.cck-note { display: flex; gap: 8px; align-items: flex-start; font-size: 12.5px; line-height: 1.45; color: var(--cck-soft); }
.cck-note svg { flex: none; margin-top: 1px; }
@keyframes cckIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .cck-root { animation: none; } }
`;

const PATHS = {
  leaf: 'M5 19c.5-8.5 5.5-13.5 14-14-.5 8.5-5.5 13.5-14 14zM5 19l7.5-7.5',
  water: 'M12 3.5s6 6.3 6 10.5a6 6 0 0 1-12 0c0-4.2 6-10.5 6-10.5z',
  stretch: 'M13.5 4.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zM4.5 8.5l7.5 2 7.5-2M12 10.5v4.5l-3.5 5M12 15l3.5 5',
  sun: 'M16 12a4 4 0 1 1-8 0 4 4 0 0 1 8 0zM12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M5.3 18.7l1.4-1.4M17.3 6.7l1.4-1.4',
  volX: 'M4 9.5h3.5L12 5.5v13l-4.5-4H4zM16 9.5l5 5M21 9.5l-5 5',
  lock: 'M6.5 11h11v9h-11zM8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  sparkle: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9zM19 15.5l.8 1.7 1.7.8-1.7.8-.8 1.7-.8-1.7-1.7-.8 1.7-.8z',
  check: 'M5 12.5l4.5 4.5L19 7',
  heart: 'M12 20s-7.5-4.6-7.5-10.2A4.3 4.3 0 0 1 12 7.3a4.3 4.3 0 0 1 7.5 2.5C19.5 15.4 12 20 12 20z',
  phone: 'M6.5 3.5h2.8l1.6 4.4-2.1 1.3a11.5 11.5 0 0 0 6 6l1.3-2.1 4.4 1.6v2.8a2 2 0 0 1-2.2 2A16 16 0 0 1 4.5 5.7a2 2 0 0 1 2-2.2z',
  text: 'M4 5h16v11H10l-4.5 4v-4H4z',
  globe: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zM3 12h18M12 3c2.4 2.5 3.6 5.5 3.6 9s-1.2 6.5-3.6 9c-2.4-2.5-3.6-5.5-3.6-9S9.6 5.5 12 3z',
  info: 'M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0zM12 11v5.5M12 7.8h.01',
};

export const CareIcon = ({ name, size = 17, stroke = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d={PATHS[name]} />
  </svg>
);

const Opt = ({ children, onClick, ...rest }) => (
  <button type="button" data-prop-ui="" className="cck-opt" onClick={onClick} {...rest}>{children}</button>
);

export default function CheckInCard({ roomId, heavy, enteredAt, onLighter, onMuteSounds, now, storage }) {
  // wait · ask · break · lighter · never · closed. The state is KEYED BY ROOM
  // (review 09-29): a page that keeps this mounted across rooms must never paint
  // the last room's card in the new one, not even for the one frame before an
  // effect could reset it. Another room's state reads as a fresh 'wait'.
  const [view, setView] = useState(() => ({ room: roomId, phase: 'wait', muted: false }));
  const mine = view.room === roomId;
  const phase = mine ? view.phase : 'wait';
  const muted = mine ? view.muted : false;
  const setPhase = (next) => setView((v) => ({ room: roomId, phase: next, muted: v.room === roomId ? v.muted : false }));
  const setMuted = (m) => setView((v) => ({ room: roomId, phase: v.room === roomId ? v.phase : 'wait', muted: m }));
  const [tick, setTick] = useState(0);
  const rootRef = useRef(null);
  const movedRef = useRef(false); // focus follows a TAP, never the card's own arrival
  const titleId = `cck-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const store = storage === undefined ? careStore() : storage;

  // a new room starts over (the page may keep this mounted across rooms)
  useEffect(() => { movedRef.current = false; }, [roomId]);

  useEffect(() => {
    if (phase !== 'wait') return undefined;
    const t = Number.isFinite(now) ? now : Date.now();
    if (checkInDue({ roomId, heavy, enteredAt, now: t, store })) {
      markCheckInAsked(store, roomId, t); // on screen = today's one ask
      setView({ room: roomId, phase: 'ask', muted: false });
      return undefined;
    }
    // Not yet: with a live clock, wake at the 20-minute mark and look again.
    if (Number.isFinite(now) || heavy !== true || !roomId) return undefined;
    const wait = msUntilCheckIn({ enteredAt, now: t });
    if (wait === null || wait <= 0) return undefined; // due-but-asked/retired: nothing to wait for
    const timer = setTimeout(() => setTick((n) => n + 1), Math.min(wait + 250, 2147483000));
    return () => clearTimeout(timer);
  }, [phase, roomId, heavy, enteredAt, now, store, tick]);

  useEffect(() => {
    if (movedRef.current && rootRef.current && ['break', 'lighter', 'never', 'ask'].includes(phase)) {
      try { rootRef.current.focus({ preventScroll: true }); } catch (e) { /* old browsers */ }
    }
  }, [phase]);

  if (phase === 'wait' || phase === 'closed') return null;

  const go = (next) => { movedRef.current = true; setPhase(next); };
  const close = () => setPhase('closed');
  const clock = () => (Number.isFinite(now) ? now : Date.now());

  const foot = (right) => (
    <div className="cck-ft">
      <CareIcon name="lock" size={12} stroke={2.2} />
      <span>{CHECKIN_COPY.only}</span>
      <span className="cck-sp" />
      {right}
    </div>
  );
  const head = (icon, text) => (
    <div className="cck-hd">
      <span className="cck-ico"><CareIcon name={icon} /></span>
      <p className="cck-ttl" id={titleId}>{text}</p>
    </div>
  );
  const closeLink = <button type="button" data-prop-ui="" className="cck-lnk" onClick={close}>{CHECKIN_COPY.close}</button>;

  let inner;
  if (phase === 'ask') {
    inner = (
      <>
        {head('leaf', CHECKIN_COPY.title)}
        <p className="cck-bd">{CHECKIN_COPY.body}</p>
        <div className="cck-row">
          <Opt data-care-choice="good" onClick={close}>{CHECKIN_COPY.good}</Opt>
          <Opt data-care-choice="lighter" onClick={() => { go('lighter'); if (typeof onLighter === 'function') onLighter(); }}>{CHECKIN_COPY.lighter}</Opt>
          <Opt data-care-choice="break" onClick={() => go('break')}>{CHECKIN_COPY.pause}</Opt>
        </div>
        {foot(
          <button type="button" data-prop-ui="" className="cck-lnk" data-care-choice="never"
            onClick={() => { retireCheckIn(store, roomId, clock()); go('never'); }}>
            {CHECKIN_COPY.never}
          </button>
        )}
      </>
    );
  } else if (phase === 'break') {
    const canMute = typeof onMuteSounds === 'function';
    inner = (
      <>
        {head('leaf', CHECKIN_COPY.breakTitle)}
        <div className="cck-tips">
          <span><CareIcon name="water" size={15} />{CHECKIN_COPY.tips[0]}</span>
          <span><CareIcon name="stretch" size={15} />{CHECKIN_COPY.tips[1]}</span>
          <span><CareIcon name="sun" size={15} />{CHECKIN_COPY.tips[2]}</span>
        </div>
        {canMute && (
          <div className="cck-row">
            <Opt data-care-choice="mute" aria-pressed={muted} disabled={muted}
              onClick={() => { if (muted) return; setMuted(true); onMuteSounds(MUTE_MS); }}>
              <CareIcon name={muted ? 'check' : 'volX'} size={16} />
              {muted ? CHECKIN_COPY.muted : CHECKIN_COPY.mute}
            </Opt>
          </div>
        )}
        {foot(closeLink)}
      </>
    );
  } else if (phase === 'lighter') {
    inner = (
      <>
        {head('sparkle', CHECKIN_COPY.lighterTitle)}
        {foot(closeLink)}
      </>
    );
  } else {
    inner = (
      <>
        {head('check', CHECKIN_COPY.neverTitle)}
        {foot(
          <>
            <button type="button" data-prop-ui="" className="cck-lnk" data-care-choice="undo"
              onClick={() => { unretireCheckIn(store, roomId); go('ask'); }}>
              {CHECKIN_COPY.undo}
            </button>
            {closeLink}
          </>
        )}
      </>
    );
  }

  return (
    <div ref={rootRef} className="cck-root" role="region" aria-labelledby={titleId} aria-live="polite"
      tabIndex={-1} data-care-checkin={phase}>
      <style>{CARE_CSS}</style>
      {inner}
    </div>
  );
}
